import { useState } from "react";
import { Box, Card, CircularProgress, Input, Typography } from "@mui/joy";
import { Check, Pencil } from "lucide-react";
import { useMeasurement } from "../../../../hooks/query";

export function MobileView(props: { data: any; handleEdit: any }) {
    const { data, handleEdit } = props;

    return (
        <Box sx={{ width: "100%", mt: 1 }}>
            <Typography sx={{ fontWeight: 500, mb: 1 }} level="body-sm" gutterBottom>
                Roof Measurements
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
                {data.map((item: any) => (
                    <MobileMeasurementTile 
                        key={item.name} 
                        item={item} 
                        onLocalEdit={handleEdit} 
                    />
                ))}
            </Box>
        </Box>
    );
}

function MobileMeasurementTile({ item, onLocalEdit }: { item: any; onLocalEdit: (e: any) => void }) {
    const value = Number(item.actual) || 0;
    const predicted = Number(item.total) || 0;
    const diff = predicted - value;

    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    
    const { useUpdateSingleMeasurement } = useMeasurement();
    const updateSvc = useUpdateSingleMeasurement();

    const performUpdate = async () => {
        if (!item.id) {
            setIsEditing(false);
            return;
        }
        try {
            const params = {
                Id: item.id,
                ActualValue: Number(editValue),
            };
            await updateSvc.mutateAsync(params);
            onLocalEdit({ id: item.id, actual: Number(editValue) });
            setIsEditing(false);
        } catch (error) {
            console.error(error);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") performUpdate();
        if (e.key === "Escape") {
            setEditValue(value);
            setIsEditing(false);
        }
    };

    return (
        <Card variant="outlined" sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 0.5, bgcolor: "background.surface" }}>
            <Box>
                <Typography level="title-sm" sx={{ color: "text.primary", fontWeight: "xl" }} noWrap>{item.name}</Typography>
                <Typography level="body-xs" sx={{ 
                    color: "text.tertiary", 
                    fontSize: "10px", 
                    lineHeight: 1.2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    mt: 0.25
                }}>{item.description}</Typography>
            </Box>
            
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", mt: 0.5, minHeight: 32 }}>
                {isEditing ? (
                    <Input
                        autoFocus
                        size="sm"
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(Number(e.target.value))}
                        onKeyDown={handleKeyDown}
                        onBlur={performUpdate}
                        endDecorator={
                            updateSvc.isPending ? <CircularProgress size="sm" /> : <Check size={14} style={{ color: "var(--joy-palette-success-500)" }}/>
                        }
                        sx={{ width: "100%", px: 0.5, py: 0 }}
                    />
                ) : (
                    <Box 
                        onClick={() => { if (item.id) setIsEditing(true); }} 
                        sx={{ 
                            display: "flex", 
                            alignItems: "baseline", 
                            gap: 0.5, 
                            cursor: item.id ? "pointer" : "default"
                        }}
                    >
                        <Typography level="title-lg" sx={{ fontWeight: "xl" }}>{value}</Typography>
                        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>{item.unit}</Typography>
                        {item.id && <Pencil size={10} style={{ opacity: 0.3, marginLeft: 2, alignSelf: "center" }} />}
                    </Box>
                )}
            </Box>
            
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5, pt: 0.75, borderTop: "1px solid", borderColor: "divider" }}>
                <Typography level="body-xs" sx={{ color: "text.tertiary", fontSize: "10px" }}>Calc: {predicted}</Typography>
                {diff !== 0 && (
                    <Typography level="body-xs" sx={{ 
                        fontWeight: 600, fontSize: "10px",
                        color: diff > 0 ? "success.500" : "danger.500" 
                    }}>
                        {diff > 0 ? "+" : ""}{diff}
                    </Typography>
                )}
            </Box>
        </Card>
    );
}
